<script lang="ts" setup>
	import { type Sizes } from "~/utils/enums";

	const props = withDefaults(
		defineProps<{
			size?: Sizes;
			state?: "red-semi" | "red" | "grey" | "disabled" | "error";
			loading?: boolean;
			error?: boolean;
			warning?: boolean;
		}>(),
		{
			size: "small",
			state: "red",
			loading: false,
			error: false,
			warning: false,
		}
	);

	const emit = defineEmits<{
		(e: "click"): void;
	}>();

	// Button heights are 32/36/40px, but the spinner sizes that share their names
	// are 20/28/48px — `large` alone is taller than the button it sits in, so it
	// spilled out of the ring. Each button takes the largest spinner that fits.
	const spinnerSizeByButtonSize: Record<Sizes, Sizes> = {
		small: "small",
		medium: "medium",
		large: "medium",
		"extra-large": "medium",
	};

	const spinnerSize = computed(() => spinnerSizeByButtonSize[props.size]);

	const getArrowColor = computed(() => {
		if (props.state === "red-semi" || props.state === "grey") {
			return "black";
		} else {
			return "white";
		}
	});
</script>

<template>
	<button @click="emit('click')" type="button" class="button flex-horizontal" :class="[size, state, { warning: warning }, { error: error }, { loading: loading }]">
		<span><slot /></span>

		<spinner v-if="loading" :size="spinnerSize" :color="getArrowColor" />
	</button>
</template>

<style scoped lang="scss">
	// Three sizes on one radius, weight 500 rather than bold, and a hover/press
	// response on every variant. `red` is the primary action, `red-semi` the
	// tinted secondary, `grey` the neutral outline.
	.button {
		position: relative;
		user-select: none;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		width: fit-content;
		border: 1px solid transparent;
		border-radius: var(--radius-md);
		@include GoogleSans(false);
		@include FontWeightMedium(false);
		letter-spacing: -0.1px;
		white-space: nowrap;
		cursor: pointer;
		transition:
			background var(--dur-fast) var(--ease-out),
			border-color var(--dur-fast) var(--ease-out),
			color var(--dur-fast) var(--ease-out),
			transform var(--dur-fast) var(--ease-out);

		span {
			transition: opacity var(--dur-fast) var(--ease-out);
		}

		&.loading {
			span {
				opacity: 0;
			}
		}

		.spinner {
			position: absolute;
			@include FadeIn(0.3s);
		}

		&:active:not(.disabled):not(.error) {
			transform: translateY(0.5px) scale(0.99);
		}

		&:focus-visible {
			outline: 2px solid var(--focus);
			outline-offset: 2px;
		}

		&.small {
			height: 32px;
			padding: 0 14px;
			font-size: 13px;
		}

		&.medium {
			height: 36px;
			padding: 0 16px;
			font-size: 14px;
		}

		&.large {
			height: 40px;
			padding: 0 20px;
			font-size: 15px;
		}

		// Phone: the two action sizes grow to a comfortable thumb target. `small`
		// stays put; it is used inline in dense rows where 32px is intended.
		@include phone {
			&.medium {
				height: 42px;
				font-size: 15px;
			}

			&.large {
				height: 46px;
			}
		}

		&.red {
			background: var(--accent);
			color: var(--text-inverse);

			&:hover {
				background: var(--accent-hover);
			}
		}

		&.red-semi {
			background: var(--accent-soft);
			color: var(--accent);

			&:hover {
				background: var(--accent-tint);
			}
		}

		/* The app's secondary button. Several call sites hand-patched the missing
		   border with class="border-grey"; this makes that the default. */
		&.grey {
			border-color: var(--border);
			background: var(--bg-surface);
			color: var(--text);

			&:hover {
				background: var(--bg-subtle);
				border-color: var(--border-strong);
			}
		}

		&.disabled {
			background: var(--bg-muted);
			color: var(--text-faint);
			pointer-events: none;
		}

		&.error {
			background: var(--accent-soft);
			color: var(--accent);
			pointer-events: none;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.button,
		.button span {
			transition: none;
		}
	}
</style>
